
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import { 
  Calendar,
  Clock,
  Play,
  Pause,
  RotateCcw,
  TimerReset,
  Save,
  Briefcase,
  Tag
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDistance } from "date-fns";
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from "recharts";

const TimeTracking = () => {
  const { toast } = useToast();
  const [isTracking, setIsTracking] = useState(false);
  const [currentTask, setCurrentTask] = useState("");
  const [currentProject, setCurrentProject] = useState("");
  const [currentNotes, setCurrentNotes] = useState("");
  const [selectedTimeEntry, setSelectedTimeEntry] = useState<number | null>(null);
  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState<number | null>(null);
  const [timeEntries, setTimeEntries] = useState([
    {
      id: 1,
      project: "E-commerce Website",
      task: "Frontend Development",
      notes: "Working on product listing page",
      startTime: new Date(Date.now() - 86400000 * 3).toISOString(),
      endTime: new Date(Date.now() - 86400000 * 3 + 10800000).toISOString(),
      duration: 10800000, // 3 hours in milliseconds
      billable: true,
      invoiced: false,
      tags: ["frontend", "react"]
    },
    {
      id: 2,
      project: "Mobile App UI",
      task: "Design Implementation",
      notes: "Converting Figma designs to Flutter components",
      startTime: new Date(Date.now() - 86400000 * 2).toISOString(),
      endTime: new Date(Date.now() - 86400000 * 2 + 7200000).toISOString(),
      duration: 7200000, // 2 hours in milliseconds
      billable: true,
      invoiced: true,
      tags: ["design", "flutter"]
    },
    {
      id: 3,
      project: "E-commerce Website",
      task: "API Integration",
      notes: "Connecting product API with frontend components",
      startTime: new Date(Date.now() - 86400000).toISOString(),
      endTime: new Date(Date.now() - 86400000 + 18000000).toISOString(),
      duration: 18000000, // 5 hours in milliseconds
      billable: true,
      invoiced: false,
      tags: ["backend", "api"]
    }
  ]);

  const projects = [
    "E-commerce Website",
    "Mobile App UI",
    "Data Analytics Dashboard",
    "Blog Migration",
    "CRM System"
  ];

  const weeklyData = [
    { day: "Mon", hours: 4.5 },
    { day: "Tue", hours: 6 },
    { day: "Wed", hours: 8 },
    { day: "Thu", hours: 5.5 },
    { day: "Fri", hours: 7 },
    { day: "Sat", hours: 3 },
    { day: "Sun", hours: 0 }
  ];

  const projectData = [
    { name: "E-commerce Website", hours: 8 },
    { name: "Mobile App UI", hours: 2 },
    { name: "Data Analytics Dashboard", hours: 5 },
    { name: "Blog Migration", hours: 4 },
    { name: "CRM System", hours: 10 }
  ];

  const handleStartTimer = () => {
    if (!currentProject || !currentTask) {
      toast({
        title: "Missing Information",
        description: "Please select a project and enter a task description.",
        variant: "destructive",
      });
      return;
    }

    setIsTracking(true);
    const interval = window.setInterval(() => {
      setTimer(prev => prev + 1000);
    }, 1000);
    setTimerInterval(interval);
  };

  const handleStopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setIsTracking(false);
  };

  const handleResetTimer = () => {
    handleStopTimer();
    setTimer(0);
    setCurrentTask("");
    setCurrentNotes("");
  };

  const handleSaveTimeEntry = () => {
    if (!currentProject || !currentTask) {
      toast({
        title: "Missing Information",
        description: "Please select a project and enter a task description.",
        variant: "destructive",
      });
      return;
    }

    if (timer === 0) {
      toast({
        title: "No Time Tracked",
        description: "Timer is at 0. Please track some time before saving.",
        variant: "destructive",
      });
      return;
    }

    const newEntry = {
      id: timeEntries.length + 1,
      project: currentProject,
      task: currentTask,
      notes: currentNotes,
      startTime: new Date(Date.now() - timer).toISOString(),
      endTime: new Date().toISOString(),
      duration: timer,
      billable: true,
      invoiced: false,
      tags: ["work"]
    };

    setTimeEntries([newEntry, ...timeEntries]);
    
    toast({
      title: "Time Entry Saved",
      description: `Saved ${formatTime(timer)} for ${currentTask}`,
    });
    
    handleResetTimer();
  };

  const handleDeleteTimeEntry = (id: number) => {
    setTimeEntries(timeEntries.filter(entry => entry.id !== id));
    toast({
      title: "Entry Deleted",
      description: "Time entry has been removed",
    });
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor(ms / (1000 * 60 * 60));

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getTotalHours = () => {
    return timeEntries.reduce((total, entry) => total + entry.duration, 0) / (1000 * 60 * 60);
  };

  const getBillableHours = () => {
    return timeEntries
      .filter(entry => entry.billable)
      .reduce((total, entry) => total + entry.duration, 0) / (1000 * 60 * 60);
  };

  const getUnbilledHours = () => {
    return timeEntries
      .filter(entry => entry.billable && !entry.invoiced)
      .reduce((total, entry) => total + entry.duration, 0) / (1000 * 60 * 60);
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gradient mb-2">Time Tracking</h1>
          <p className="text-gray-600">Track, manage, and report your work time efficiently</p>
        </div>
        <Button variant="outline" className="mt-4 md:mt-0">
          <Calendar className="mr-2 h-4 w-4" />
          View Reports
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="glass-effect">
          <CardHeader className="pb-2">
            <CardTitle className="text-md font-medium">Total Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-2xl font-bold">{getTotalHours().toFixed(1)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Across all projects</p>
          </CardContent>
        </Card>
        
        <Card className="glass-effect">
          <CardHeader className="pb-2">
            <CardTitle className="text-md font-medium">Billable Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Briefcase className="h-5 w-5 text-emerald-500 mr-2" />
              <span className="text-2xl font-bold">{getBillableHours().toFixed(1)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Hours that can be charged</p>
          </CardContent>
        </Card>
        
        <Card className="glass-effect">
          <CardHeader className="pb-2">
            <CardTitle className="text-md font-medium">Unbilled Hours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Tag className="h-5 w-5 text-amber-500 mr-2" />
              <span className="text-2xl font-bold">{getUnbilledHours().toFixed(1)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Hours not yet invoiced</p>
          </CardContent>
        </Card>
        
        <Card className="glass-effect">
          <CardHeader className="pb-2">
            <CardTitle className="text-md font-medium">Current Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-purple-500 mr-2" />
              <span className="text-2xl font-bold">34.5</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Hours worked this week</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="glass-effect lg:col-span-1">
          <CardHeader>
            <CardTitle>Timer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="project">Project</Label>
                <Select 
                  value={currentProject} 
                  onValueChange={setCurrentProject}
                  disabled={isTracking}
                >
                  <SelectTrigger id="project">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map(project => (
                      <SelectItem key={project} value={project}>
                        {project}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="task">Task</Label>
                <Input
                  id="task"
                  placeholder="What are you working on?"
                  value={currentTask}
                  onChange={(e) => setCurrentTask(e.target.value)}
                  disabled={isTracking}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="Add details about your work..."
                  value={currentNotes}
                  onChange={(e) => setCurrentNotes(e.target.value)}
                />
              </div>
              
              <div className="text-center py-6">
                <div className="text-4xl font-mono font-bold mb-4">
                  {formatTime(timer)}
                </div>
                <div className="flex justify-center gap-3">
                  {!isTracking ? (
                    <Button 
                      onClick={handleStartTimer}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Play className="mr-2 h-4 w-4" />
                      Start
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleStopTimer}
                      variant="destructive"
                    >
                      <Pause className="mr-2 h-4 w-4" />
                      Pause
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={handleResetTimer}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Reset
                  </Button>
                </div>
              </div>
              
              <Button 
                className="w-full bg-blue-500 hover:bg-blue-600" 
                onClick={handleSaveTimeEntry}
                disabled={isTracking}
              >
                <Save className="mr-2 h-4 w-4" />
                Save Time Entry
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-effect lg:col-span-2">
          <CardHeader>
            <CardTitle>Weekly Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weeklyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="day" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value) => [`${value} hours`, "Time"]}
                  />
                  <Bar
                    dataKey="hours"
                    fill="#8b5cf6"
                    radius={[4, 4, 0, 0]}
                    name="Hours"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="glass-effect lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Time Entries</CardTitle>
            <Select defaultValue="all">
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(project => (
                  <SelectItem key={project} value={project}>{project}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {timeEntries.map((entry) => (
                <div 
                  key={entry.id} 
                  className={`border rounded-lg p-4 ${
                    selectedTimeEntry === entry.id 
                      ? "border-blue-300 bg-blue-50" 
                      : "border-gray-200 hover:border-blue-200 hover:bg-gray-50"
                  } transition-all cursor-pointer`}
                  onClick={() => setSelectedTimeEntry(
                    selectedTimeEntry === entry.id ? null : entry.id
                  )}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-gray-500" />
                        <h3 className="font-medium">{entry.project}</h3>
                      </div>
                      <p className="text-sm text-gray-600">{entry.task}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-mono font-medium">
                          {formatTime(entry.duration)}
                        </div>
                        <p className="text-xs text-gray-500">
                          {formatDistance(
                            new Date(entry.endTime),
                            new Date(entry.startTime),
                            { addSuffix: false }
                          )}
                        </p>
                      </div>
                      <Badge variant={entry.billable ? (entry.invoiced ? "outline" : "default") : "secondary"}>
                        {entry.billable ? (entry.invoiced ? "Invoiced" : "Billable") : "Non-billable"}
                      </Badge>
                    </div>
                  </div>
                  
                  {selectedTimeEntry === entry.id && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Start Time</p>
                          <p className="text-sm">{formatDate(entry.startTime)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">End Time</p>
                          <p className="text-sm">{formatDate(entry.endTime)}</p>
                        </div>
                      </div>
                      {entry.notes && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500">Notes</p>
                          <p className="text-sm bg-gray-50 p-2 rounded">{entry.notes}</p>
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        {entry.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="bg-gray-100">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" size="sm">Edit</Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTimeEntry(entry.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-effect">
          <CardHeader>
            <CardTitle>Time by Project</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={projectData}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#6b7280" />
                  <YAxis 
                    dataKey="name" 
                    type="category" 
                    stroke="#6b7280" 
                    width={100}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    }}
                    formatter={(value) => [`${value} hours`, "Time"]}
                  />
                  <Bar
                    dataKey="hours"
                    fill="#0ea5e9"
                    radius={[0, 4, 4, 0]}
                    name="Hours"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TimeTracking;
